import { useEffect, useRef, useState, useCallback } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { SearchAddon } from '@xterm/addon-search';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { io, Socket } from 'socket.io-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Eraser, RotateCcw, X, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';

import '@xterm/xterm/css/xterm.css';

interface ShellTabProps {
  projectId: string;
  branchId: number;
}

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export function ShellTab({ projectId, branchId }: ShellTabProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstance = useRef<Terminal | null>(null);
  const fitAddon = useRef<FitAddon | null>(null);
  const searchAddon = useRef<SearchAddon | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [searchTerm, setSearchTerm] = useState('');

  // Prevent double-initialization in React StrictMode dev
  const initializedRef = useRef(false);
  const isDisposedRef = useRef(false);

  // Initialize terminal and socket connection
  const initializeTerminal = useCallback(() => {
    if (!terminalRef.current) return;
    if (initializedRef.current) return;
    initializedRef.current = true;
    isDisposedRef.current = false;
    const container = terminalRef.current;
    if (!container.isConnected) return;

    // Clean up existing terminal
    if (terminalInstance.current) {
      terminalInstance.current.dispose();
    }

    // Create terminal with VS Code-like theme
    const terminal = new Terminal({
      cursorBlink: true,
      cursorStyle: 'block',
      fontFamily: '"Cascadia Code", "Fira Code", "JetBrains Mono", "SF Mono", Monaco, "Courier New", monospace',
      fontSize: 13,
      fontWeight: 400,
      fontWeightBold: 700,
      lineHeight: 1.2,
      letterSpacing: 0,
      scrollback: 10000,
      tabStopWidth: 4,
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
        cursor: '#aeafad',
        cursorAccent: '#1e1e1e',
        selectionBackground: '#264f78',
        selectionForeground: '#ffffff',
        black: '#000000',
        red: '#cd3131',
        green: '#0dbc79',
        yellow: '#e5e510',
        blue: '#2472c8',
        magenta: '#bc3fbc',
        cyan: '#11a8cd',
        white: '#e5e5e5',
        brightBlack: '#666666',
        brightRed: '#f14c4c',
        brightGreen: '#23d18b',
        brightYellow: '#f5f543',
        brightBlue: '#3b8eea',
        brightMagenta: '#d670d6',
        brightCyan: '#29b8db',
        brightWhite: '#e5e5e5'
      },
      allowTransparency: false,
      minimumContrastRatio: 4.5,
    });

    // Initialize addons
    fitAddon.current = new FitAddon();
    searchAddon.current = new SearchAddon();
    const webLinksAddon = new WebLinksAddon();

    terminal.loadAddon(fitAddon.current);
    terminal.loadAddon(searchAddon.current);
    terminal.loadAddon(webLinksAddon);

    // Open terminal in container - defer if container has no dimensions yet
    const openTerminal = () => {
      try {
        terminal.open(container);
        if (fitAddon.current && container.offsetWidth > 0 && container.offsetHeight > 0) {
          fitAddon.current.fit();
        }
      } catch {
        // ignore transient open/fit errors (xterm.js dimensions issue)
      }
    };

    if (container.offsetWidth === 0 || container.offsetHeight === 0) {
      requestAnimationFrame(openTerminal);
    } else {
      openTerminal();
    }

    // Show connecting message
    terminal.writeln('\x1B[1;36mConnecting to shell...\x1B[0m');

    terminalInstance.current = terminal;

    // Connect to socket
    connectSocket(terminal);

    // Handle window resize
    const handleResize = () => {
      if (
        fitAddon.current &&
        terminalInstance.current?.element?.isConnected &&
        container.offsetWidth > 0 &&
        container.offsetHeight > 0
      ) {
        try {
          fitAddon.current.fit();
          sendTerminalSize();
        } catch {
          // ignore transient resize errors when terminal is not ready
        }
      }
    };
    window.addEventListener('resize', handleResize);

    // Keyboard shortcuts
    terminal.attachCustomKeyEventHandler((event) => {
      // Ctrl/Cmd + C for copy when text is selected
      if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
        if (terminal.hasSelection()) {
          const selection = terminal.getSelection();
          navigator.clipboard.writeText(selection).catch(() => {});
          return false;
        }
        return true; // Let Ctrl+C pass through as interrupt
      }

      // Ctrl/Cmd + F for search
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        // Focus search input
        const searchInput = document.getElementById('shell-search-input');
        if (searchInput) {
          searchInput.focus();
          (searchInput as HTMLInputElement).select();
        }
        return false;
      }

      // Ctrl/Cmd + L to clear
      if ((event.ctrlKey || event.metaKey) && event.key === 'l') {
        terminal.clear();
        return false;
      }

      return true;
    });

    // Initial fit after a short delay
    setTimeout(() => {
      if (
        fitAddon.current &&
        terminalInstance.current?.element?.isConnected &&
        container.offsetWidth > 0 &&
        container.offsetHeight > 0
      ) {
        try {
          fitAddon.current.fit();
          sendTerminalSize();
        } catch (err) {
          // ignore transient fit errors
        }
      }
    }, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const connectSocket = useCallback((terminal: Terminal) => {
    // Clean up existing socket
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    setStatus('connecting');

    // Create socket connection
    // Always use same origin to go through the proxy server
    // This ensures WebSocket connections work from browser → frontend proxy → backend
    const socketBase = window.location.origin;

    const socket = io(socketBase, {
      path: '/socket.io/',
      withCredentials: true,
      transports: ['websocket'],
      upgrade: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 60000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setStatus('connected');
      
      // Start shell session
      socket.emit('start_shell_session', {
        deployment_id: branchId,
        project_id: projectId,
        cols: terminal.cols,
        rows: terminal.rows,
      });
    });

    socket.on('shell_output', (data: { output: string }) => {
      terminal.write(data.output);
    });

    socket.on('shell_error', (data: { error: string }) => {
      terminal.writeln(`\r\n\x1B[1;31mError: ${data.error}\x1B[0m\r\n`);
      setStatus('error');
    });

    socket.on('disconnect', () => {
      setStatus('disconnected');
      terminal.writeln('\r\n\x1B[1;31mDisconnected. Attempting to reconnect...\x1B[0m\r\n');
    });

    socket.on('connect_error', () => {
      setStatus('error');
      terminal.writeln('\r\n\x1B[1;31mConnection error.\x1B[0m\r\n');
    });

    // Handle terminal input
    terminal.onData((data) => {
      if (socket.connected) {
        socket.emit('shell_input', {
          input: data,
          deployment_id: branchId,
        });
      }
    });

    // Handle terminal resize
    terminal.onResize(() => {
      if (!isDisposedRef.current) {
        sendTerminalSize();
      }
    });
  }, [branchId, projectId]);

  const sendTerminalSize = useCallback(() => {
    if (
      socketRef.current?.connected &&
      terminalInstance.current &&
      !isDisposedRef.current &&
      terminalInstance.current.element?.isConnected
    ) {
      try {
        socketRef.current.emit('resize_terminal', {
          deployment_id: branchId,
          cols: terminalInstance.current.cols,
          rows: terminalInstance.current.rows,
        });
      } catch (err) {
        // ignore transient resize errors during cleanup
      }
    }
  }, [branchId]);

  const handleClearTerminal = useCallback(() => {
    terminalInstance.current?.clear();
  }, []);

  const handleResetShell = useCallback(() => {
    if (socketRef.current && terminalInstance.current) {
      terminalInstance.current.clear();
      terminalInstance.current.writeln('\x1B[1;33mResetting shell session...\x1B[0m\r\n');

      // Disconnect and reconnect
      socketRef.current.emit('stop_shell_session', { deployment_id: branchId });
      socketRef.current.disconnect();

      setTimeout(() => {
        if (terminalInstance.current) {
          connectSocket(terminalInstance.current);
        }
      }, 500);
    }
  }, [branchId, connectSocket]);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    if (searchAddon.current && term) {
      searchAddon.current.findNext(term);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Initialize on mount
  useEffect(() => {
    const cleanup = initializeTerminal();

    return () => {
      initializedRef.current = false;
      isDisposedRef.current = true;
      cleanup?.();
      if (socketRef.current) {
        socketRef.current.emit('stop_shell_session', { deployment_id: branchId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (terminalInstance.current) {
        terminalInstance.current.dispose();
        terminalInstance.current = null;
      }
    };
  }, [branchId, initializeTerminal]);

  // Re-fit terminal when tab becomes visible
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      const el = terminalRef.current;
      if (
        fitAddon.current &&
        terminalInstance.current &&
        !isDisposedRef.current &&
        terminalInstance.current.element?.isConnected &&
        el &&
        el.offsetWidth > 0 &&
        el.offsetHeight > 0
      ) {
        try {
          fitAddon.current.fit();
          sendTerminalSize();
        } catch (error) {
          // Terminal may have been disposed during resize, ignore
        }
      }
    });

    if (terminalRef.current) {
      resizeObserver.observe(terminalRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [sendTerminalSize]);

  const statusColors: Record<ConnectionStatus, string> = {
    connecting: 'text-yellow-500',
    connected: 'text-green-500',
    disconnected: 'text-red-500',
    error: 'text-red-500',
  };

  const statusLabels: Record<ConnectionStatus, string> = {
    connecting: 'Connecting',
    connected: 'Connected',
    disconnected: 'Disconnected',
    error: 'Error',
  };

  return (
    <div className="mt-4">
      {/* Shell Controls */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearTerminal}
            title="Clear Terminal"
          >
            <Eraser className="h-4 w-4 mr-1" />
            Clear
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetShell}
            className="text-red-600 border-red-200 hover:bg-red-50"
            title="Reset Shell Session"
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset
          </Button>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative w-48">
            <Input
              id="shell-search-input"
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="h-8 pr-8"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Badge */}
          <Badge variant="secondary" className="py-1 px-2">
            <Circle className={cn('h-2 w-2 mr-1.5 fill-current', statusColors[status])} />
            <span className="text-xs">{statusLabels[status]}</span>
          </Badge>
        </div>
      </div>

      {/* Terminal Container */}
      <div
        className="rounded-lg overflow-hidden border border-gray-700"
        style={{
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
          background: '#1e1e1e',
        }}
      >
        <div
          ref={terminalRef}
          className="w-full p-2"
          style={{
            height: 'calc(100vh - 400px)',
            minHeight: '500px',
            background: '#1e1e1e',
          }}
        />
      </div>
    </div>
  );
}
